/* Fitness Society theme JS — reveals, header, drawer hooks. Vanilla, no deps. */
(() => {
  'use strict';

  /* Scroll reveals */
  const initReveals = () => {
    const els = document.querySelectorAll('.reveal');
    if (!els.length) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      els.forEach((el) => el.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -5% 0px' });
    els.forEach((el) => io.observe(el));
  };

  /* Header: shadow on scroll, mobile menu toggle */
  const initHeader = () => {
    const header = document.querySelector('[data-header]');
    if (!header) return;
    const onScrollEdge = new IntersectionObserver(([e]) => {
      header.classList.toggle('is-scrolled', !e.isIntersecting);
    });
    const sentinel = document.createElement('div');
    sentinel.style.cssText = 'position:absolute;top:0;height:1px;width:1px;';
    document.body.prepend(sentinel);
    onScrollEdge.observe(sentinel);

    const toggle = header.querySelector('[data-menu-toggle]');
    const drawer = header.querySelector('[data-menu-drawer]');
    if (toggle && drawer) {
      toggle.addEventListener('click', () => {
        const open = header.classList.toggle('menu-open');
        toggle.setAttribute('aria-expanded', open);
        drawer.toggleAttribute('aria-hidden', !open);
        document.documentElement.classList.toggle('no-scroll', open);
      });
      drawer.addEventListener('keydown', (ev) => {
        if (ev.key === 'Escape' && header.classList.contains('menu-open')) toggle.click();
      });
    }
  };

  /* Product: variant selection, quantity, sticky mobile ATC */
  const initProduct = () => {
    const root = document.querySelector('[data-product-section]');
    if (!root) return;

    let variants = [];
    try { variants = JSON.parse(root.querySelector('[data-variant-json]').textContent); } catch (e) { variants = []; }

    const form = root.querySelector('[data-product-form]');
    const select = root.querySelector('[data-variant-select]');
    const priceEl = root.querySelector('[data-price]');
    const atc = root.querySelector('[data-atc]');
    const atcText = root.querySelector('[data-atc-text]');
    const stickyPrice = root.querySelector('[data-sticky-price]');
    const stickyBtn = root.querySelector('[data-sticky-btn]');
    const optionInputs = root.querySelectorAll('[data-option-index]');
    const mainImg = root.querySelector('#ProductMainImage');
    const thumbs = root.querySelectorAll('[data-thumb]');

    const swapMainImage = (url) => {
      if (!mainImg || !url) return;
      const sized = url.includes('width=') ? url : url + (url.includes('?') ? '&' : '?') + 'width=1200';
      mainImg.src = sized;
      mainImg.srcset = '';
    };

    /* thumbnail click -> swap main image */
    thumbs.forEach((t) => {
      t.addEventListener('click', () => {
        thumbs.forEach((x) => x.classList.remove('is-active'));
        t.classList.add('is-active');
        swapMainImage(t.dataset.mediaUrl);
      });
    });

    const money = (cents) => {
      try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: window.Shopify?.currency?.active || 'USD' }).format(cents / 100); }
      catch (e) { return '$' + (cents / 100).toFixed(2); }
    };

    const findVariant = () => {
      if (!optionInputs.length) return variants[0];
      const chosen = [];
      root.querySelectorAll('[data-option-index]:checked').forEach((i) => { chosen[+i.dataset.optionIndex] = i.value; });
      return variants.find((v) => v.options.every((opt, idx) => opt === chosen[idx]));
    };

    const updateVariant = () => {
      const v = findVariant();
      if (!v) {
        if (atc) { atc.disabled = true; }
        if (atcText) atcText.textContent = 'Unavailable';
        return;
      }
      if (select) select.value = v.id;
      if (priceEl) {
        const sale = v.compare_at_price && v.compare_at_price > v.price;
        priceEl.innerHTML = sale
          ? '<span class="price price--sale"><span class="price__current">' + money(v.price) + '</span><s class="price__compare">' + money(v.compare_at_price) + '</s></span>'
          : '<span class="price"><span class="price__current">' + money(v.price) + '</span></span>';
      }
      if (stickyPrice) stickyPrice.textContent = money(v.price);
      /* swap to variant image + sync active thumb */
      if (v.featured_image && v.featured_image.src) {
        swapMainImage(v.featured_image.src);
        const pos = v.featured_image.position;
        thumbs.forEach((t, i) => t.classList.toggle('is-active', i === (pos - 1)));
      }
      const avail = v.available;
      [atc, stickyBtn].forEach((b) => { if (b) b.disabled = !avail; });
      if (atcText) atcText.textContent = avail ? 'Add to cart' : 'Sold out';
      const url = new URL(window.location);
      url.searchParams.set('variant', v.id);
      window.history.replaceState({}, '', url);
    };

    optionInputs.forEach((i) => i.addEventListener('change', updateVariant));

    /* subscribe & save: toggle selling_plan select on/off */
    const planSelect = root.querySelector('[data-selling-plan]');
    if (planSelect) {
      root.querySelectorAll('[data-plan-mode]').forEach((r) => {
        r.addEventListener('change', () => {
          const sub = root.querySelector('[data-plan-mode][value="subscribe"]').checked;
          planSelect.disabled = !sub;
        });
      });
    }

    /* quantity */
    const qty = root.querySelector('[data-qty-input]');
    const qtyMax = () => { const m = parseInt(qty.getAttribute('max'), 10); return m > 0 ? m : Infinity; };
    root.querySelector('[data-qty-minus]')?.addEventListener('click', () => { qty.value = Math.max(1, (+qty.value || 1) - 1); });
    root.querySelector('[data-qty-plus]')?.addEventListener('click', () => { qty.value = Math.min(qtyMax(), (+qty.value || 1) + 1); });

    /* sticky mobile ATC: show once main ATC scrolls out */
    const sticky = root.querySelector('[data-sticky-atc]');
    if (sticky && atc) {
      const io = new IntersectionObserver(([e]) => {
        const show = !e.isIntersecting;
        sticky.hidden = false;
        sticky.classList.toggle('is-visible', show);
      }, { rootMargin: '0px 0px -100% 0px' });
      io.observe(atc);
    }

    /* add to cart -> open drawer (drawer wired separately) */
    if (form) {
      form.addEventListener('submit', (ev) => {
        if (!window.__fsCartDrawer) return; /* fall back to /cart navigation */
        ev.preventDefault();
        const btn = ev.submitter || atc;
        const data = new FormData(form);
        if (btn) btn.classList.add('is-loading');
        fetch(window.Shopify.routes.root + 'cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: data })
          .then((r) => r.json())
          .then(() => window.__fsCartDrawer.refresh(true))
          .catch(() => { form.submit(); })
          .finally(() => { if (btn) btn.classList.remove('is-loading'); });
      });
    }
  };

  /* Cart: drawer + cart page share contents. Updates via Section Rendering API. */
  const initCart = () => {
    const drawer = document.querySelector('[data-cart-drawer]');
    const root = window.Shopify?.routes?.root || '/';
    const headerCount = document.querySelector('[data-cart-count]');

    const open = () => {
      if (!drawer) return;
      drawer.setAttribute('aria-hidden', 'false');
      drawer.classList.add('is-open');
      document.documentElement.classList.add('no-scroll');
    };
    const close = () => {
      if (!drawer) return;
      drawer.setAttribute('aria-hidden', 'true');
      drawer.classList.remove('is-open');
      document.documentElement.classList.remove('no-scroll');
    };

    const render = (html) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-cart-body]');
      if (!fresh) return;
      document.querySelectorAll('[data-cart-body]').forEach((el) => { el.innerHTML = fresh.innerHTML; });
    };

    const updateCounts = (count) => {
      document.querySelectorAll('[data-cart-count-label]').forEach((el) => { el.textContent = '(' + count + ')'; });
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = count;
        el.classList.toggle('is-empty', count === 0);
      });
    };

    const refresh = (andOpen) => {
      return fetch(root + '?sections=cart-live', { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((data) => { if (data['cart-live']) render(data['cart-live']); })
        .then(() => fetch(root + 'cart.js').then((r) => r.json()))
        .then((cart) => { updateCounts(cart.item_count); if (andOpen) open(); })
        .catch(() => {});
    };

    const change = (line, qty) => {
      return fetch(root + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: line, quantity: qty, sections: 'cart-live' }),
      })
        .then((r) => r.json())
        .then((cart) => {
          if (cart && typeof cart.item_count === 'number') {
            if (cart.sections && cart.sections['cart-live']) render(cart.sections['cart-live']);
            updateCounts(cart.item_count);
          }
        })
        .catch(() => {});
    };

    /* delegate cart-item interactions across drawer + cart page */
    document.addEventListener('click', (ev) => {
      if (ev.target.closest('[data-cart-close]')) { close(); return; }
      const item = ev.target.closest('[data-cart-item]');
      if (!item) return;
      const line = +item.dataset.line || 1;
      const max = parseInt(item.dataset.max, 10);
      const qtyEl = item.querySelector('[data-cart-qty]');
      const cur = qtyEl ? parseInt(qtyEl.textContent, 10) || 1 : 1;
      if (ev.target.closest('[data-cart-plus]')) { if (!(max > 0) || cur < max) change(line, cur + 1); }
      else if (ev.target.closest('[data-cart-minus]')) change(line, Math.max(0, cur - 1));
      else if (ev.target.closest('[data-cart-remove]')) change(line, 0);
    });

    document.addEventListener('keydown', (ev) => {
      if (ev.key === 'Escape' && drawer && drawer.classList.contains('is-open')) close();
    });

    /* header cart link opens drawer (only when drawer exists) */
    if (drawer) {
      const cartLink = document.querySelector('.header__cart');
      if (cartLink) cartLink.addEventListener('click', (ev) => { ev.preventDefault(); refresh(true); });
      window.__fsCartDrawer = { open, close, refresh };
    }
  };

  /* Predictive search: debounced suggestions via Shopify predictive API */
  const initSearch = () => {
    const form = document.querySelector('[data-predictive-search]');
    if (!form) return;
    const input = form.querySelector('[data-predictive-input]');
    const results = form.querySelector('[data-predictive-results]');
    const root = window.Shopify?.routes?.root || '/';
    let timer;

    const money = (cents) => {
      try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: window.Shopify?.currency?.active || 'USD' }).format(cents / 100); }
      catch (e) { return '$' + (cents / 100).toFixed(2); }
    };

    const hide = () => { results.hidden = true; results.innerHTML = ''; };

    const query = (term) => {
      fetch(root + 'search/suggest.json?q=' + encodeURIComponent(term) + '&resources[type]=product&resources[limit]=6')
        .then((r) => r.json())
        .then((data) => {
          const products = (data.resources?.results?.products) || [];
          if (!products.length) { results.innerHTML = '<p class="predictive__empty">No matches. Press Enter to search everything.</p>'; results.hidden = false; return; }
          results.innerHTML = products.map((p) => {
            const img = p.featured_image?.url ? '<img class="predictive__img" src="' + p.featured_image.url + '&width=88" alt="" width="44" height="44" loading="lazy">' : '<span class="predictive__img"></span>';
            const price = p.price ? '<span class="predictive__price">' + money(p.price) + '</span>' : '';
            return '<a class="predictive__item" href="' + p.url + '">' + img + '<span class="predictive__info"><span class="predictive__title">' + p.title + '</span>' + price + '</span></a>';
          }).join('');
          results.hidden = false;
        })
        .catch(hide);
    };

    input.addEventListener('input', () => {
      const term = input.value.trim();
      clearTimeout(timer);
      if (term.length < 2) { hide(); return; }
      timer = setTimeout(() => query(term), 220);
    });
    document.addEventListener('click', (ev) => { if (!form.contains(ev.target)) hide(); });
    input.addEventListener('keydown', (ev) => { if (ev.key === 'Escape') hide(); });
  };

  /* UGC video carousels: play on click, arrow scroll, pause others */
  const initVideoCarousels = () => {
    document.querySelectorAll('[data-vcar]').forEach((car) => {
      const rail = car.querySelector('[data-vcar-rail]');
      car.querySelectorAll('[data-vcard-play]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const frame = btn.closest('[data-vcard]');
          const video = frame.querySelector('video');
          if (!video) return;
          car.querySelectorAll('video').forEach((v) => { if (v !== video) { v.pause(); v.closest('[data-vcard]')?.classList.remove('is-playing'); } });
          frame.classList.add('is-playing');
          video.play();
        });
      });
      const step = () => (rail ? rail.clientWidth * 0.8 : 300);
      car.querySelector('[data-vcar-next]')?.addEventListener('click', () => rail.scrollBy({ left: step(), behavior: 'smooth' }));
      car.querySelector('[data-vcar-prev]')?.addEventListener('click', () => rail.scrollBy({ left: -step(), behavior: 'smooth' }));
    });
  };

  /* Product tabs + read-more clamp */
  const initTabs = () => {
    const root = document.querySelector('[data-tabs]');
    if (!root) return;
    const tabs = [...root.querySelectorAll('[data-tab]')];
    const panels = [...root.querySelectorAll('[data-panel]')];
    const show = (key) => {
      tabs.forEach((t) => t.setAttribute('aria-selected', t.dataset.tab === key));
      panels.forEach((p) => { p.hidden = p.dataset.panel !== key; });
    };
    tabs.forEach((t) => t.addEventListener('click', () => show(t.dataset.tab)));
    if (tabs.length) show(tabs[0].dataset.tab);

    const clamp = root.querySelector('[data-clamp]');
    const more = root.querySelector('[data-readmore]');
    if (clamp && more) {
      if (clamp.scrollHeight <= clamp.clientHeight + 4) { more.hidden = true; }
      more.addEventListener('click', () => {
        const open = clamp.classList.toggle('is-open');
        more.textContent = open ? 'Read less' : 'Read more';
      });
    }
  };

  /* Article TOC: build from H2s, scroll-spy active link */
  const initToc = () => {
    const nav = document.querySelector('[data-toc-nav]');
    const body = document.querySelector('[data-article-body]');
    if (!nav || !body) return;
    const headings = [...body.querySelectorAll('h2')];
    if (headings.length < 2) { document.querySelector('[data-toc]')?.remove(); return; }

    headings.forEach((h, i) => {
      if (!h.id) h.id = 'section-' + (i + 1) + '-' + h.textContent.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40);
      const a = document.createElement('a');
      a.href = '#' + h.id;
      a.textContent = h.textContent;
      a.dataset.tocLink = h.id;
      nav.appendChild(a);
    });

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const links = nav.querySelectorAll('[data-toc-link]');
    const spy = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          links.forEach((l) => l.classList.toggle('is-active', l.dataset.tocLink === e.target.id));
        }
      });
    }, { rootMargin: '-20% 0px -70% 0px' });
    headings.forEach((h) => spy.observe(h));
  };

  /* Supplement quiz: step through questions, map first answer -> result */
  const initQuiz = () => {
    const quiz = document.querySelector('[data-quiz]');
    if (!quiz) return;
    const panels = [...quiz.querySelectorAll('[data-quiz-panel]')];
    const results = [...quiz.querySelectorAll('[data-quiz-result]')];
    const bar = quiz.querySelector('[data-quiz-bar]');
    const stepLabel = quiz.querySelector('[data-quiz-step]');
    const emailBox = quiz.querySelector('[data-quiz-email]');
    if (!panels.length) return;

    let current = 0;
    let goalValue = null;

    const showPanel = (i) => {
      panels.forEach((p, idx) => p.classList.toggle('is-active', idx === i));
      results.forEach((r) => { r.classList.remove('is-active'); r.hidden = true; });
      if (emailBox) { emailBox.hidden = true; emailBox.classList.remove('is-active'); }
      current = i;
      const pct = Math.round(((i) / panels.length) * 100);
      if (bar) bar.style.width = pct + '%';
      if (stepLabel) stepLabel.textContent = 'Question ' + (i + 1) + ' of ' + panels.length;
    };

    const showResult = (key) => {
      panels.forEach((p) => p.classList.remove('is-active'));
      if (bar) bar.style.width = '100%';
      if (stepLabel) stepLabel.textContent = 'Your result';
      let match = results.find((r) => r.dataset.quizResult === key) || results[0];
      results.forEach((r) => { const on = r === match; r.hidden = !on; r.classList.toggle('is-active', on); });
      if (emailBox) { emailBox.hidden = false; emailBox.classList.add('is-active'); }
      quiz.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    quiz.addEventListener('click', (ev) => {
      const ans = ev.target.closest('[data-quiz-answer]');
      if (ans) {
        const panel = ans.closest('[data-quiz-panel]');
        if (panel && panel.hasAttribute('data-quiz-goal')) goalValue = ans.dataset.value;
        if (current < panels.length - 1) showPanel(current + 1);
        else showResult(goalValue);
        return;
      }
      if (ev.target.closest('[data-quiz-back]')) { if (current > 0) showPanel(current - 1); return; }
      if (ev.target.closest('[data-quiz-restart]')) { goalValue = null; showPanel(0); return; }
    });

    /* prefill from ?goal= */
    const goalParam = new URLSearchParams(window.location.search).get('goal');
    if (goalParam) {
      const goalPanel = quiz.querySelector('[data-quiz-goal]');
      const match = goalPanel && goalPanel.querySelector('[data-quiz-answer][data-value="' + goalParam + '"]');
      if (match) { goalValue = goalParam; if (panels.length > 1) showPanel(1); else showResult(goalValue); }
    }
  };

  /* Product recommendations: lazy-load via Shopify recommendations API */
  const initRecs = () => {
    const el = document.querySelector('[data-product-recs]');
    if (!el || el.querySelector('.bestseller__rail')) return;
    const url = el.dataset.url;
    if (!url) return;
    fetch(url)
      .then((r) => r.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-product-recs]');
        if (fresh && fresh.querySelector('.bestseller__rail')) {
          el.innerHTML = fresh.innerHTML;
          initReveals();
        } else {
          el.remove();
        }
      })
      .catch(() => el.remove());
  };

  document.addEventListener('DOMContentLoaded', () => {
    initReveals();
    initHeader();
    initProduct();
    initCart();
    initSearch();
    initToc();
    initQuiz();
    initRecs();
    initTabs();
    initVideoCarousels();
  });

  /* re-init reveals + product for Shopify editor section reloads */
  document.addEventListener('shopify:section:load', () => { initReveals(); initProduct(); });
})();
